'use client';

import { useState } from 'react';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

import Link from "next/link";
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { userApplicationService } from '@/lib/services/userApplicationService';

export default function BeAUserPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    // Contact Details
    address: '',
    city: '',
    province: 'Ontario',
    postalCode: '',
    alternatePhone: '',
    // Service Preferences
    servicePreferences: [] as string[],
    otherServiceDescription: '',
    // Moving Needs
    typicalItemTypes: '',
    averageItemSize: 'medium',
    requiresAssemblyDisassembly: false,
    requiresPackingMaterials: false,
    // Frequency of Use
    frequencyOfUse: 'as_needed',
    estimatedMonthlyBookings: 0,
    // Preferred Payment Method
    preferredPaymentMethod: 'credit_card',
    // Special Requirements
    accessibilityRequirements: '',
    specialHandlingNeeds: '',
    preferredTimeSlots: [] as string[],
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    // Screening Questions
    howDidYouHear: '',
    whyChooseGogedit: '',
    previousMovingExperience: '',
    additionalComments: '',
    // Terms
    termsAccepted: false,
    privacyPolicyAccepted: false,
    marketingConsent: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleServicePreferenceChange = (service: string) => {
    if (formData.servicePreferences.includes(service)) {
      setFormData({
        ...formData,
        servicePreferences: formData.servicePreferences.filter((s) => s !== service)
      });
    } else {
      setFormData({
        ...formData,
        servicePreferences: [...formData.servicePreferences, service]
      });
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.firstName && formData.lastName && formData.email &&
        formData.phone && formData.dateOfBirth &&
        formData.address && formData.city && formData.province && formData.postalCode);
      case 2:
        return formData.servicePreferences.length > 0;
      case 3:
        return true; // Moving needs are optional
      case 4:
        return !!(formData.frequencyOfUse && formData.preferredPaymentMethod);
      case 5:
        return true; // Special requirements are optional
      case 6:
        return !!(formData.emergencyContactName && formData.emergencyContactPhone &&
        formData.emergencyContactRelationship);
      case 7:
        return !!(formData.howDidYouHear && formData.whyChooseGogedit);
      case 8:
        return formData.termsAccepted && formData.privacyPolicyAccepted;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert('Please fill in all required fields before proceeding.');
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(8)) {
      alert('Please complete all required fields and accept the terms.');
      return;
    }

    setLoading(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const result = await userApplicationService.submitApplication({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode,
        alternatePhone: formData.alternatePhone || undefined,
        servicePreferences: formData.servicePreferences,
        otherServiceDescription: formData.otherServiceDescription || undefined,
        typicalItemTypes: formData.typicalItemTypes || undefined,
        averageItemSize: formData.averageItemSize || undefined,
        requiresAssemblyDisassembly: formData.requiresAssemblyDisassembly,
        requiresPackingMaterials: formData.requiresPackingMaterials,
        frequencyOfUse: formData.frequencyOfUse,
        estimatedMonthlyBookings: formData.estimatedMonthlyBookings || undefined,
        preferredPaymentMethod: formData.preferredPaymentMethod,
        accessibilityRequirements: formData.accessibilityRequirements || undefined,
        specialHandlingNeeds: formData.specialHandlingNeeds || undefined,
        preferredTimeSlots: formData.preferredTimeSlots.length > 0 ? formData.preferredTimeSlots : undefined,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone,
        emergencyContactRelationship: formData.emergencyContactRelationship,
        howDidYouHear: formData.howDidYouHear,
        whyChooseGogedit: formData.whyChooseGogedit,
        previousMovingExperience: formData.previousMovingExperience || undefined,
        additionalComments: formData.additionalComments || undefined,
        termsAccepted: formData.termsAccepted,
        privacyPolicyAccepted: formData.privacyPolicyAccepted,
        marketingConsent: formData.marketingConsent
      });

      if (result) {
        setSubmitStatus('success');
        setCurrentStep(9); // Success step

        // Send confirmation email — fire-and-forget, does not block success flow
        fetch('/api/email/application-confirmation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            applicationType: 'user',
            applicantDetails: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              city: formData.city,
              province: formData.province,
              servicePreferences: formData.servicePreferences,
              frequencyOfUse: formData.frequencyOfUse,
              preferredPaymentMethod: formData.preferredPaymentMethod,
            },
          }),
        }).catch((emailErr) => {
          console.warn('[Email] Failed to send user confirmation email:', emailErr);
        });
      } else {
        setSubmitStatus('error');
        setErrorMessage('Failed to submit your application. Please try again.');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setErrorMessage(error?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: `${SITE_URL}/home-page` },
  { name: 'Be a User', url: `${SITE_URL}/be-a-user` }]
  );

  const serviceOptions = [
  { value: 'furniture_delivery', label: 'Furniture Delivery' },
  { value: 'appliance_moving', label: 'Appliance Moving' },
  { value: 'marketplace_pickup', label: 'Marketplace Pickup' },
  { value: 'office_moving', label: 'Office Moving' },
  { value: 'storage_delivery', label: 'Storage Delivery' },
  { value: 'other', label: 'Other' }];


  return (
    <div className="min-h-screen bg-white">
      {/* Structured Data - WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Be a User - Join GoGedit Community',
            description: 'Register as a GoGedit user and access reliable moving and delivery services across Ontario.',
            url: `${SITE_URL}/be-a-user`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: SITE_URL
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Be a User',
                item: `${SITE_URL}/be-a-user`
              }]

            },
            mainEntity: {
              '@type': 'Service',
              name: 'GoGedit User Registration',
              provider: {
                '@type': 'Organization',
                name: 'GoGedit',
                url: SITE_URL
              },
              serviceType: 'Moving and Delivery Services',
              areaServed: {
                '@type': 'State',
                name: 'Ontario'
              }
            }
          })
        }} />
      

      {/* Structured Data - Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'GoGedit',
            url: SITE_URL,
            logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1134114a2-1771779843742.png",
            description: 'Ontario\'s leading on-demand moving and delivery platform',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'Customer Service',
              areaServed: 'CA-ON'
            }
          })
        }} />
      

      {/* Structured Data - FAQPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
            {
              '@type': 'Question',
              name: 'How do I register as a GoGedit user?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Complete our comprehensive user registration form with your personal information, contact details, service preferences, and moving needs. The application process takes approximately 10-15 minutes.'
              }
            },
            {
              '@type': 'Question',
              name: 'What services can I access as a GoGedit user?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'As a registered user, you can access furniture delivery, appliance moving, marketplace pickup services, and other on-demand moving and delivery services across Ontario.'
              }
            },
            {
              '@type': 'Question',
              name: 'Is there a fee to register as a user?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'No, user registration with GoGedit is completely free. You only pay for the services you book.'
              }
            },
            {
              '@type': 'Question',
              name: 'What areas does GoGedit serve?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'GoGedit provides moving and delivery services across Ontario, including major cities and surrounding areas.'
              }
            }]

          })
        }} />
      

      <NavBar />
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-navy-dark mb-4">Be a User with GoGedit</h1>
            <p className="text-xl text-body-text">
              Join Ontario's leading on-demand moving and delivery platform
            </p>
          </div>

          {/* Progress Bar */}
          {currentStep < 9 &&
          <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-navy-dark">Step {currentStep} of 8</span>
                <span className="text-sm text-body-text">{Math.round(currentStep / 8 * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                className="bg-gradient-to-r from-brand-purple to-brand-green h-3 rounded-full transition-all duration-500"
                style={{ width: `${currentStep / 8 * 100}%` }}>
              </div>
              </div>
            </div>
          }

          {/* Success Message */}
          {currentStep === 9 &&
          <div className="bg-white border-2 border-brand-green rounded-2xl p-12 text-center shadow-lg">
              <div className="w-20 h-20 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-navy-dark mb-4">Application Submitted Successfully!</h2>
              <p className="text-lg text-body-text mb-8">
                Thank you for applying to be a GoGedit user. Our team will review your application and contact you within 2-3 business days.
              </p>
              <div className="space-y-4">
                <a
                href="/home-page"
                className="inline-block bg-brand-purple text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all">
                
                  Return to Home
                </a>
              </div>
            </div>
          }

          {/* Form Steps */}
          {currentStep < 9 &&
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
              <form onSubmit={handleSubmit}>
                {/* Step 1: Personal Information & Contact Details */}
                {currentStep === 1 &&
              <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-bold text-navy-dark mb-2">Personal Information</h2>
                      <p className="text-body-text mb-6">Let's start with your basic information and contact details</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          First Name *
                        </label>
                        <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Last Name *
                        </label>
                        <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Email Address *
                        </label>
                        <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Phone Number *
                        </label>
                        <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="416-555-0123"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Street Address *
                      </label>
                      <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          City *
                        </label>
                        <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Province *
                        </label>
                        <select
                      name="province"
                      required
                      value={formData.province}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent">
                      
                          <option value="Ontario">Ontario</option>
                          <option value="Quebec">Quebec</option>
                          <option value="British Columbia">British Columbia</option>
                          <option value="Alberta">Alberta</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Postal Code *
                        </label>
                        <input
                      type="text"
                      name="postalCode"
                      required
                      value={formData.postalCode}
                      onChange={handleChange}
                      placeholder="M5V 2T6"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Date of Birth *
                        </label>
                        <input
                      type="date"
                      name="dateOfBirth"
                      required
                      min="1920-01-01"
                      max={new Date().toISOString().split('T')[0]}
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Alternate Phone (Optional)
                        </label>
                        <input
                      type="tel"
                      name="alternatePhone"
                      value={formData.alternatePhone}
                      onChange={handleChange}
                      placeholder="647-555-0123"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                    
                      </div>
                    </div>
                  </div>
              }

                {/* Step 2: Service Preferences */}
                {currentStep === 2 &&
              <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy-dark mb-6">Service Preferences</h2>
                    <p className="text-body-text mb-4">Select all services you're interested in *</p>
                    
                    <div className="space-y-3">
                      {serviceOptions.map((service) =>
                  <div key={service.value} className="flex items-start">
                          <input
                      type="checkbox"
                      id={service.value}
                      checked={formData.servicePreferences.includes(service.value)}
                      onChange={() => handleServicePreferenceChange(service.value)}
                      className="mt-1 mr-3 w-5 h-5 text-brand-purple focus:ring-brand-purple" />
                    
                          <label htmlFor={service.value} className="text-navy-dark font-medium">
                            {service.label}
                          </label>
                        </div>
                  )}
                    </div>

                    {formData.servicePreferences.includes('other') &&
                <div>
                        <label className="block text-sm font-medium text-navy-dark mb-2">
                          Please describe other services
                        </label>
                        <textarea
                    name="otherServiceDescription"
                    value={formData.otherServiceDescription}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                      </div>
                }
                  </div>
              }

                {/* Step 3: Moving Needs */}
                {currentStep === 3 &&
              <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy-dark mb-6">Moving Needs</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Typical Item Types (Optional)
                      </label>
                      <textarea
                    name="typicalItemTypes"
                    value={formData.typicalItemTypes}
                    onChange={handleChange}
                    placeholder="e.g., Sofas, tables, refrigerators, marketplace purchases"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Average Item Size (Optional)
                      </label>
                      <select
                    name="averageItemSize"
                    value={formData.averageItemSize}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent">
                    
                        <option value="small">Small (boxes, small furniture)</option>
                        <option value="medium">Medium (chairs, tables, small appliances)</option>
                        <option value="large">Large (sofas, refrigerators, large furniture)</option>
                        <option value="extra_large">Extra Large (large appliances, large rental equipment)</option>
                      </select>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start">
                        <input
                      type="checkbox"
                      id="requiresAssemblyDisassembly"
                      name="requiresAssemblyDisassembly"
                      checked={formData.requiresAssemblyDisassembly}
                      onChange={handleChange}
                      className="mt-1 mr-3 w-5 h-5 text-brand-purple focus:ring-brand-purple" />
                    
                        <label htmlFor="requiresAssemblyDisassembly" className="text-navy-dark">
                          I may need assembly/disassembly services
                        </label>
                      </div>
                      <div className="flex items-start">
                        <input
                      type="checkbox"
                      id="requiresPackingMaterials"
                      name="requiresPackingMaterials"
                      checked={formData.requiresPackingMaterials}
                      onChange={handleChange}
                      className="mt-1 mr-3 w-5 h-5 text-brand-purple focus:ring-brand-purple" />
                    
                        <label htmlFor="requiresPackingMaterials" className="text-navy-dark">
                          I may need packing materials
                        </label>
                      </div>
                    </div>
                  </div>
              }

                {/* Step 4: Frequency and Payment */}
                {currentStep === 4 &&
              <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy-dark mb-6">Frequency & Payment</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        How often will you use our services? *
                      </label>
                      <select
                    name="frequencyOfUse"
                    required
                    value={formData.frequencyOfUse}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent">
                    
                        <option value="one_time">One-time use</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="as_needed">As needed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Estimated Monthly Bookings (Optional)
                      </label>
                      <input
                    type="number"
                    name="estimatedMonthlyBookings"
                    min="0"
                    value={formData.estimatedMonthlyBookings}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Preferred Payment Method *
                      </label>
                      <select
                    name="preferredPaymentMethod"
                    required
                    value={formData.preferredPaymentMethod}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent">
                    
                        <option value="credit_card">Credit Card</option>
                        <option value="debit_card">Debit Card</option>
                        <option value="invoice">Invoice (for businesses ONLY)</option>
                      </select>
                    </div>
                  </div>
              }

                {/* Step 5: Special Requirements */}
                {currentStep === 5 &&
              <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy-dark mb-6">Special Requirements</h2>
                    <p className="text-body-text mb-4">All fields are optional but help us serve you better</p>
                    
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Driver Accesibility / Pick up Localtion Details 
                      </label>
                      <textarea
                    name="accessibilityRequirements"
                    value={formData.accessibilityRequirements}
                    onChange={handleChange}
                    placeholder="e.g., Elevator access required, ground floor only, stairs"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Special Handling Needs
                      </label>
                      <textarea
                    name="specialHandlingNeeds"
                    value={formData.specialHandlingNeeds}
                    onChange={handleChange}
                    placeholder="e.g., Fragile items, antiques, temperature-sensitive items"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Preferred Time Slots
                      </label>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {['Weekend', 'Weekday', 'AM', 'PM', 'Overnight', 'Flexible'].map((slot) => (
                          <label key={slot} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              name="preferredTimeSlots"
                              value={slot}
                              checked={formData.preferredTimeSlots.includes(slot)}
                              onChange={(e) => {
                                const { value, checked } = e.target;
                                setFormData((prev) => ({
                                  ...prev,
                                  preferredTimeSlots: checked
                                    ? [...prev.preferredTimeSlots, value]
                                    : prev.preferredTimeSlots.filter((s) => s !== value),
                                }));
                              }}
                              className="w-4 h-4 accent-brand-purple rounded"
                            />
                            <span className="text-sm text-navy-dark">{slot}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
              }

                {/* Step 6: Emergency Contact */}
                {currentStep === 6 &&
              <div className="space-y-6">
<h2 className="text-2xl font-bold text-navy-dark mb-6">Emergency Contact</h2>
<p className="text-sm text-gray-600 -mt-4 mb-2">
  We collect emergency contact information solely for your safety. In the unlikely event of an emergency during a service, we may need to reach someone on your behalf. This information is kept confidential and will never be shared or used for any other purpose.
</p>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
Emergency Contact Name *
                      </label>
                      <input
                    type="text"
                    name="emergencyContactName"
                    required
                    value={formData.emergencyContactName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
Emergency Contact Phone *
                      </label>
                      <input
                    type="tel"
                    name="emergencyContactPhone"
                    required
                    value={formData.emergencyContactPhone}
                    onChange={handleChange}
                    placeholder="416-555-0123"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
Relationship *
                      </label>
                      <input
                    type="text"
                    name="emergencyContactRelationship"
                    required
                    value={formData.emergencyContactRelationship}
                    onChange={handleChange}
                    placeholder="e.g., Spouse, Parent, Sibling, Friend"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>
                  </div>
              }

                {/* Step 7: Screening Questions */}
                {currentStep === 7 &&
              <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy-dark mb-6">Tell Us About Yourself</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        How did you hear about GoGedit? *
                      </label>
                      <input
                    type="text"
                    name="howDidYouHear"
                    required
                    value={formData.howDidYouHear}
                    onChange={handleChange}
                    placeholder="e.g., Google, Facebook, Friend referral, Advertisement"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Why did you choose GoGedit? *
                      </label>
                      <textarea
                    name="whyChooseGogedit"
                    required
                    value={formData.whyChooseGogedit}
                    onChange={handleChange}
                    placeholder="Tell us what attracted you to our service"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Previous Moving Experience (Optional)
                      </label>
                      <textarea
                    name="previousMovingExperience"
                    value={formData.previousMovingExperience}
                    onChange={handleChange}
                    placeholder="Share your experience with moving services"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-dark mb-2">
                        Additional Comments (Optional)
                      </label>
                      <textarea
                    name="additionalComments"
                    value={formData.additionalComments}
                    onChange={handleChange}
                    placeholder="Any other information you'd like to share"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent" />
                  
                    </div>
                  </div>
              }

                {/* Step 8: Terms and Conditions */}
                {currentStep === 8 &&
              <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-navy-dark mb-6">Terms & Conditions</h2>
                    
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <input
                      type="checkbox"
                      id="termsAccepted"
                      name="termsAccepted"
                      required
                      checked={formData.termsAccepted}
                      onChange={handleChange}
                      className="mt-1 mr-3 w-5 h-5 text-brand-purple focus:ring-brand-purple" />
                    
                        <label htmlFor="termsAccepted" className="text-navy-dark">
                          I agree to the <Link href="/terms-and-conditions" target="_blank" className="text-brand-purple hover:underline">Terms & Conditions</Link> *
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                      type="checkbox"
                      id="privacyPolicyAccepted"
                      name="privacyPolicyAccepted"
                      required
                      checked={formData.privacyPolicyAccepted}
                      onChange={handleChange}
                      className="mt-1 mr-3 w-5 h-5 text-brand-purple focus:ring-brand-purple" />
                    
                        <label htmlFor="privacyPolicyAccepted" className="text-navy-dark">
                          I agree to the <Link href="/privacy-policy" target="_blank" className="text-brand-purple hover:underline">Privacy Policy</Link> *
                        </label>
                      </div>

                      <div className="flex items-start">
                        <input
                      type="checkbox"
                      id="marketingConsent"
                      name="marketingConsent"
                      checked={formData.marketingConsent}
                      onChange={handleChange}
                      className="mt-1 mr-3 w-5 h-5 text-brand-purple focus:ring-brand-purple" />
                    
                        <label htmlFor="marketingConsent" className="text-navy-dark">
                          I would like to receive promotional emails and updates from GoGedit (optional)
                        </label>
                      </div>
                    </div>

                    {submitStatus === 'error' &&
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800">{errorMessage}</p>
                      </div>
                }
                  </div>
              }

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  {currentStep > 1 && currentStep < 9 &&
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-3 border-2 border-gray-300 text-navy-dark rounded-full font-medium hover:bg-gray-50 transition-all">
                  
                      Previous
                    </button>
                }
                  {currentStep < 8 &&
                <button
                  type="button"
                  onClick={nextStep}
                  className="ml-auto px-8 py-3 bg-brand-purple text-white rounded-full font-medium hover:bg-opacity-90 transition-all">
                  
                      Next
                    </button>
                }
                  {currentStep === 8 &&
                <button
                  type="submit"
                  disabled={loading}
                  className="ml-auto px-8 py-3 bg-brand-green text-white rounded-full font-medium hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                  
                      {loading ? 'Submitting...' : 'Submit Application'}
                    </button>
                }
                </div>
              </form>
            </div>
          }
        </div>
      </div>

      <FooterSection />
    </div>);

}